import { Fragment } from 'react';
import { UserPen } from 'lucide-react';
import { detailsHistorySentence, type CustomerDetailsHistoryEntry } from '@atink/domain';
import type { CustomerRecord } from '@/domain/types';
import ConversationTimelineEntry from '@/components/ConversationTimelineEntry';
import { actorLabel } from '@/domain/actor';
import { simTimeFull } from '@/utils/format';

interface Props {
  customer: CustomerRecord;
  className?: string;
}

/**
 * Single source of truth for a customer's conversation thread: the heading,
 * the empty-state, and the timeline entries. Deliberately excludes the live
 * call / AI banners so each parent (the customer record, an upcoming work
 * modal) can position those independently.
 *
 * tv-pzvy: the record's details history ("Georgina updated the mobile (was …)")
 * sits in the timeline at the time it happened. It is never a message.
 */
export default function ConversationThread({ customer, className }: Props) {
  const history = customer.detailsHistory ?? [];
  // Each history line goes before the first message that came after it.
  const before = new Map<number, CustomerDetailsHistoryEntry[]>();
  const trailing: CustomerDetailsHistoryEntry[] = [];
  for (const h of history) {
    const at = Date.parse(h.at);
    const idx = customer.conversation.findIndex((m) => Date.parse(m.timestamp) > at);
    if (idx < 0) trailing.push(h);
    else before.set(idx, [...(before.get(idx) ?? []), h]);
  }
  return (
    <div className={className}>
      <h2 className="label-small-caps text-gold mb-4">Conversation</h2>
      {customer.conversation.length === 0 && history.length === 0 ? (
        <p className="font-serif text-cream-soft">No messages yet.</p>
      ) : null}
      {customer.conversation.map((entry, i) => (
        <Fragment key={entry.id}>
          {(before.get(i) ?? []).map((h) => (
            <DetailsHistoryLine key={h.id} entry={h} />
          ))}
          <ConversationTimelineEntry entry={entry} customer={customer} />
        </Fragment>
      ))}
      {trailing.map((h) => (
        <DetailsHistoryLine key={h.id} entry={h} />
      ))}
    </div>
  );
}

function DetailsHistoryLine({ entry }: { entry: CustomerDetailsHistoryEntry }) {
  const byName = entry.byName ?? (entry.by ? actorLabel(entry.by) : undefined);
  return (
    <p
      className="my-3 flex items-start gap-2 font-inter text-xs text-cream-soft"
      data-testid="customer-details-history"
    >
      <UserPen size={14} className="text-gold shrink-0 mt-px" aria-hidden="true" />
      <span>
        {detailsHistorySentence({ ...entry, ...(byName ? { byName } : {}) })} · {simTimeFull(entry.at)}
        {entry.fields.includes('phone') ? (
          <span className="block text-cream-faint">Reminders and confirmations go to the new mobile from then on.</span>
        ) : null}
      </span>
    </p>
  );
}
